import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Save, RefreshCw, Phone, Mail, MapPin, Building2, AlertCircle, CheckCircle2 } from "lucide-react";
import type { ContactInfo } from "@/hooks/use-supabase-data";

const emptyForm = {
  title: "",
  description: "",
  phone: "",
  email: "",
  address: "",
};

export function ContactInfoManager() {
  const [contactInfo, setContactInfo] = useState<ContactInfo | null>(null);
  const [formState, setFormState] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    fetchContactInfo();
  }, []);

  const fetchContactInfo = async () => {
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const { data, error } = await supabase
        .from("contact_info")
        .select("*")
        .order("updated_at", { ascending: false })
        .limit(1);

      if (error) throw error;

      const record = data && data.length > 0 ? (data[0] as ContactInfo) : null;
      setContactInfo(record);
      setFormState({
        title: record?.title ?? "",
        description: record?.description ?? "",
        phone: record?.phone ?? "",
        email: record?.email ?? "",
        address: record?.address ?? "",
      });
    } catch (_err) {
      setError("Failed to load contact information. Please try again.");
      setContactInfo(null);
      setFormState(emptyForm);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    setSaving(true);
    setError("");
    setSuccess("");

    try {
      if (contactInfo?.id) {
        const { error } = await supabase
          .from("contact_info")
          .update({
            ...formState,
            updated_at: new Date().toISOString(),
          })
          .eq("id", contactInfo.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("contact_info")
          .insert({
            ...formState,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          });

        if (error) throw error;
      }

      setSuccess("Contact information saved successfully.");
      await fetchContactInfo();
    } catch (_err) {
      setError("Unable to save contact information. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field: keyof typeof formState) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormState((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="space-y-2">
          <h2 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
            Contact Information
          </h2>
          <p className="text-slate-600 text-lg">
            Manage the contact and overview details shown in the site footer.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full md:w-auto">
          <Button
            variant="outline"
            onClick={fetchContactInfo}
            disabled={loading || saving}
            className="w-full sm:w-auto flex items-center gap-2 hover:bg-blue-50 hover:border-blue-200 hover:text-blue-700"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="border-green-200 bg-green-50 text-green-700">
          <CheckCircle2 className="h-4 w-4" />
          <AlertDescription>{success}</AlertDescription>
        </Alert>
      )}

      <Card className="bg-white/80 backdrop-blur-sm border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-primary" />
            Footer Contact Details
          </CardTitle>
          <CardDescription>Update the village overview and contact touchpoints.</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto mb-4" />
                <p className="text-muted-foreground">Loading contact details...</p>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="title" className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-primary" />
                    Footer Title
                  </Label>
                  <Input
                    id="title"
                    placeholder="Shanti Nagar Village"
                    value={formState.title}
                    onChange={handleChange("title")}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-primary" />
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="info@shantinagar.gov.in"
                    value={formState.email}
                    onChange={handleChange("email")}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="phone" className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-primary" />
                    Phone Number
                  </Label>
                  <Input
                    id="phone"
                    placeholder="+91 98765 43210"
                    value={formState.phone}
                    onChange={handleChange("phone")}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description" className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-primary" />
                    Highlight Statement
                  </Label>
                  <Input
                    id="description"
                    placeholder="A thriving community dedicated to progress..."
                    value={formState.description}
                    onChange={handleChange("description")}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="address" className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-primary" />
                  Address
                </Label>
                <Textarea
                  id="address"
                  placeholder="Village Office, Shanti Nagar, District Headquarters, State, India"
                  rows={3}
                  value={formState.address}
                  onChange={handleChange("address")}
                />
              </div>

              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setFormState({
                    title: contactInfo?.title ?? "",
                    description: contactInfo?.description ?? "",
                    phone: contactInfo?.phone ?? "",
                    email: contactInfo?.email ?? "",
                    address: contactInfo?.address ?? "",
                  })}
                  disabled={saving}
                >
                  Reset
                </Button>
                <Button type="submit" disabled={saving}>
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

